import {
  Container,
  UpperSection,
  TopSection,
  FilterContainer,
  PeriodFilter,
  TagFilterContainer,
  PeriodChips,
  SortFilter,
  OnlyRepeatCheckbox,
  Section,
} from "./NoshowReservationManagementTab.styles";
import UI from "@mile-meeting/mile-ui";
import NoshowListTable from "./uis/table";
import { NoShowListServices } from "./service";
import { useToast } from "hook/useToast";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TNoShowListItem, TPeriodChip, TSearchPeriod, TSortType } from "./types";
import RepeatCancelModal from "../modals/repeat-cancel-modal";
import { getKorDate } from "utils/date";
import CalendarFilter from "./uis/calendar";
import { TNoShowDetailInfo } from "../NoshowReservationDetail/types";
import { AmplitudeService } from "utils/amplitude.service";
import { useRecoilValue } from "recoil";
import { workspace } from "recoil/recoilState";

const NoshowReservationManagementTab = ({
  period,
  setPeriod,
  hide,
}: {
  period: TSearchPeriod;
  setPeriod: (period: TSearchPeriod) => void;
  hide: boolean;
}) => {
  const [selectedDetailInfo, setSelectedDetailInfo] = useState<TNoShowDetailInfo | null>(null);
  const [cancelModalOpen, setCancelModalOpen] = useState<boolean>(false);
  const [noShowList, setNoShowList] = useState<TNoShowListItem[]>([]);
  const [openCalendarModal, setOpenCalendarModal] = useState<boolean>(false);
  const calendarRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [totalPage, setTotalPage] = useState<number>(1);
  const [selectedPeriodChip, setSelectedPeriodChip] = useState<TPeriodChip | undefined>("3개월");
  const [cancelRequestModalOpen, setCancelRequestModalOpen] = useState<boolean>(false);
  const [isRepeatOnly, setIsRepeatOnly] = useState<boolean>(false); //* INFO : 반복 예약만 보기
  const [sortType, setSortType] = useState<TSortType>("COUNT"); //* INFO : 정렬 기준
  const [isCanceling, setIsCanceling] = useState<boolean>(false);

  const services = new NoShowListServices();
  const workspaceInfo = useRecoilValue(workspace);
  const periodChips = ["1개월", "3개월", "6개월", "1년"];
  const toast = useToast();
  const navigate = useNavigate();
  const amplitudeService = new AmplitudeService();

  //* INFO : 상세정보 페이지로 이동
  const navigateDetailInfo = (id: string) => {
    // alert(`/meeting/운영%2F예약%20정책%20관리/노쇼%20정책%20관리/${id}`);
    navigate(`/meeting/운영%2F예약%20정책%20관리/노쇼%20정책%20관리?id=${id}`);
    // navigate(`${id}`);
  };

  //* INFO : 반복 예약 취소
  const handleCancel = async () => {
    setIsCanceling(true);
    const success = await services.cancelNoshow(selectedDetailInfo?.instanceId ?? "");
    if (success) {
      amplitudeService.trackingCancelNoshow(
        workspaceInfo?.id ?? "",
        workspaceInfo?.name ?? "",
        selectedDetailInfo?.instanceId ?? "",
        selectedDetailInfo?.noshowData?.noshowNumInPeriod ?? 0,
        "LIST",
        selectedDetailInfo?.recurrenceData?.startAt ?? "",
        selectedDetailInfo?.recurrenceData?.terminatedAt ?? "",
        selectedDetailInfo?.recurrenceData?.rruleStr ?? "",
        selectedDetailInfo?.noshowData?.totalNoshowNum ?? 0
      );

      if (selectedDetailInfo?.googleEventId) {
        //* INFO: Google 회의실일 경우, 8초 지연
        await setTimeout(() => {}, 8000);
      }

      setNoshowList(period.startDate, period.endDate, isRepeatOnly, sortType, 1);
      toast.showToast(`"${selectedDetailInfo?.title}" 반복 예약이 취소되었습니다.`, "success");
      setCancelModalOpen(false);
      setIsCanceling(false);
    } else {
      toast.showToast(`"${selectedDetailInfo?.title}" 반복 예약 취소에 실패했습니다.`, "error");
      setIsCanceling(false);
    }
  };

  //* INFO : 반복 예약 취소 요청
  const handleCancelRequest = async () => {
    setIsCanceling(true);
    const success = await services.requestCancelNoshow(selectedDetailInfo?.instanceId ?? "");
    if (success) {
      amplitudeService.trackingRequestCancelNoshow(
        workspaceInfo?.id ?? "",
        workspaceInfo?.name ?? "",
        selectedDetailInfo?.instanceId ?? "",
        selectedDetailInfo?.noshowData?.noshowNumInPeriod ?? 0,
        "LIST",
        selectedDetailInfo?.recurrenceData?.startAt ?? "",
        selectedDetailInfo?.recurrenceData?.terminatedAt ?? "",
        selectedDetailInfo?.recurrenceData?.rruleStr ?? "",
        selectedDetailInfo?.noshowData?.totalNoshowNum ?? 0
      );

      toast.showToast(`"${selectedDetailInfo?.title}" 반복 예약의 취소 요청 알림을 전송했습니다.`, "success");
      setCancelRequestModalOpen(false);
      setIsCanceling(false);
    } else {
      toast.showToast(`"${selectedDetailInfo?.title}" 반복 예약 취소 요청 알림 전송에 실패했습니다.`, "error");
      setIsCanceling(false);
    }
  };

  //* INFO : 반복 예약 취소 모달 열기
  const onOpenCancelRequestModal = async (id: string) => {
    const detailInfo = await services.getNoshowDetailInfo(id, period.startDate, period.endDate);

    //* 반복 예약 종료일이 현재 날짜보다 이전인 경우
    if (
      detailInfo?.recurrenceData.terminatedAt ? new Date(detailInfo?.recurrenceData.terminatedAt) < new Date() : false
    ) {
      toast.showToast(`"${detailInfo?.title}" 반복 예약에 취소 가능한 잔여 예약이 없습니다.`, "error");
      return;
    }

    setSelectedDetailInfo(detailInfo);
    setCancelRequestModalOpen(true);
  };

  //* INFO : 반복 예약 취소 모달 열기
  const onOpenCancelModal = async (id: string) => {
    const detailInfo = await services.getNoshowDetailInfo(id, period.startDate, period.endDate);

    //* 반복 예약 종료일이 현재 날짜보다 이전인 경우
    if (
      detailInfo?.recurrenceData.terminatedAt ? new Date(detailInfo?.recurrenceData.terminatedAt) < new Date() : false
    ) {
      toast.showToast(`"${detailInfo?.title}" 반복 예약에 취소 가능한 잔여 예약이 없습니다.`, "error");
      return;
    }
    setSelectedDetailInfo(detailInfo);
    setCancelModalOpen(true);
  };

  //* INFO : 노쇼 예약 목록 조회
  const setNoshowList = async (
    startTime: Date,
    endTime: Date,
    isRepeatOnly: boolean,
    sortType: TSortType,
    page: number
  ) => {
    setLoading(true);

    services.getNoshowList(startTime, endTime, isRepeatOnly, sortType, page).then((data) => {
      if (data.success) {
        amplitudeService.trackingGetNoshowList(
          workspaceInfo?.id ?? "",
          workspaceInfo?.name ?? "",
          startTime,
          endTime,
          data.list.filter((item) => item.cancelability).map((item) => item.instanceId),
          page
        );
      }

      setNoShowList(data.list ?? []);
      setPage(page);
      setTotalPage(data.totalPageNumber);

      setLoading(false);
    });
  };

  useEffect(() => {
    if (!hide) setNoshowList(period.startDate, period.endDate, isRepeatOnly, sortType, 1);
  }, [hide]);

  useEffect(() => {
    const handler = (event: any) => {
      if (openCalendarModal && calendarRef.current && !calendarRef.current.contains(event.target)) {
        setOpenCalendarModal?.(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
    };
  }, [openCalendarModal]);

  if (hide) return null;
  return (
    <Container style={{ height: hide ? 0 : "100%" }}>
      <TopSection>
        <UpperSection style={{ marginTop: 20 }}>
          <FilterContainer>
            <PeriodFilter ref={calendarRef}>
              <UI.Typography type="body01 normal" fontStyle="medium" color="var(--alias-color-label-normal)">
                조회 기간
              </UI.Typography>
              <div onFocus={() => setOpenCalendarModal(true)}>
                <UI.InputGroup.TextField
                  value={getKorDate(period.startDate.toDateString()).replaceAll(".", ". ")}
                  size="small"
                  prefixIcon="calendar"
                  showPrefixIcon
                  disabledFocus
                />
              </div>
              <div style={{ width: 12, height: 1, backgroundColor: "var(--alias-color-label-normal)" }} />
              <div onFocus={() => setOpenCalendarModal(true)}>
                <UI.InputGroup.TextField
                  value={getKorDate(period.endDate.toDateString()).replaceAll(".", ". ")}
                  size="small"
                  prefixIcon="calendar"
                  showPrefixIcon
                  disabledFocus
                />
              </div>
              <CalendarFilter
                selectedDate={[period.startDate, period.endDate]}
                open={openCalendarModal}
                onChange={(startDate, endDate) => {
                  setSelectedPeriodChip(undefined);
                  setPeriod({ startDate, endDate });
                  setNoshowList(startDate, endDate, isRepeatOnly, sortType, 1);
                }}
              />
            </PeriodFilter>
          </FilterContainer>
        </UpperSection>
        <TagFilterContainer>
          <PeriodChips>
            {periodChips.map((chip, index) => (
              <UI.Chip.Outlined
                selected={selectedPeriodChip === chip}
                key={index}
                title={chip}
                onClick={() => {
                  if (selectedPeriodChip === chip) return;
                  const months = [1, 3, 6, 12];
                  setPeriod({
                    startDate: services.getStartDateByMonth(months[index]),
                    endDate: new Date(),
                  });
                  setSelectedPeriodChip(chip as TPeriodChip);
                  setNoshowList(services.getStartDateByMonth(months[index]), new Date(), isRepeatOnly, sortType, 1);
                }}
              />
            ))}
          </PeriodChips>
          <SortFilter>
            <OnlyRepeatCheckbox
              onClick={() => {
                setIsRepeatOnly(!isRepeatOnly);
                setNoshowList(period.startDate, period.endDate, !isRepeatOnly, sortType, 1);
              }}
            >
              <UI.CheckBox selected={isRepeatOnly} />
              <UI.Typography type="body01 normal" fontStyle="medium" color="var(--alias-color-label-normal)">
                반복 예약만 보기
              </UI.Typography>
            </OnlyRepeatCheckbox>
            <UI.SelectBox
              onChange={(value) => {
                if (value[0] === "노쇼 횟수순") setSortType("COUNT");
                else setSortType("DATE");

                setNoshowList(
                  period.startDate,
                  period.endDate,
                  isRepeatOnly,
                  value[0] === "노쇼 횟수순" ? "COUNT" : "DATE",
                  1
                );
              }}
              value={sortType === "COUNT" ? "노쇼 횟수순" : "최신순"}
              data={["노쇼 횟수순", "최신순"]}
              size="small"
              width={120}
              selectedData={[sortType === "COUNT" ? "노쇼 횟수순" : "최신순"]}
            />
          </SortFilter>
        </TagFilterContainer>
      </TopSection>
      <Section>
        <NoshowListTable
          selectedId={selectedDetailInfo?.id}
          isCanceling={isCanceling}
          noShowRsvList={noShowList}
          isLoading={loading}
          onHandleCancel={onOpenCancelModal}
          onHandleCancelRequest={onOpenCancelRequestModal}
          onNavigateDetailInfo={navigateDetailInfo}
        />

        {!loading && totalPage > 0 && (
          <UI.Pagination
            pageCount={totalPage}
            currentPage={page}
            onChange={(page) => {
              setPage(page);
              setNoshowList(period.startDate, period.endDate, isRepeatOnly, sortType, page);
            }}
          />
        )}
      </Section>
      <RepeatCancelModal
        type="cancel"
        reservationInfo={selectedDetailInfo}
        openModal={cancelModalOpen}
        closeModal={() => setCancelModalOpen(false)}
        onHandleCancel={handleCancel}
      />
      <RepeatCancelModal
        type="cancelRequest"
        reservationInfo={selectedDetailInfo}
        openModal={cancelRequestModalOpen}
        closeModal={() => setCancelRequestModalOpen(false)}
        onHandleCancel={handleCancelRequest}
      />
    </Container>
  );
};

export default NoshowReservationManagementTab;
